import { CancelTokenSource } from 'axios';
import { FC, useState, createContext, useContext } from 'react';
import { ChatModel, MessageModel, OthersChatModel } from 'src/models/chat';
import { GroupModel } from 'src/models/groups';
type ChatContext = {
  selectedMyChat: ChatModel;
  selectedGroup: GroupModel;
  selectedChat: OthersChatModel;
  selectMyChat: (chat: ChatModel) => void;
  selectGroup: (group: GroupModel) => void;
  selectChat: (chat: OthersChatModel) => void;
  activeTab: 'MyChat' | 'groups' | 'chats';
  setActiveTab: (tab: 'chats' | 'groups' | 'MyChat') => void;
  isGroupsArchived: boolean;
  setIsGroupsArchived: (isArchived: boolean) => void;
  isChatsArchived: boolean;
  setIsChatsArchived: (isArchived: boolean) => void;
  isMyChatsArchived: boolean;
  setIsMyChatsArchived: (isArchived: boolean) => void;
  gropsUnreadCount: number;
  chatsUnreadCount: number;
  myChatsUnreadCount: number;
  setGroupsUnreadCount: (count: number) => void;
  setChatsUnreadCount: (count: number) => void;
  setMyChatsUnreadCount: (count: number) => void;
  sendingFileChats: number[];
  setSendingFileChats: (chats: number[]) => void;
  sendingFileChatAddItem: (
    chatId: number,
    file: File,
    messageTimestamp,
    axiosSource: CancelTokenSource
  ) => void;
  sendingFileChatRemoveItem: (chatId: number) => void;
  sendingFileChatUpdateProgress: (
    messageTimeStamp: any,
    progress: number
  ) => void;
  isItemSendingFile: (chatId: number) => boolean;
  itemsSendingFile: (
    chatId: number
  ) => { progress: number; file: File; axiosSource: CancelTokenSource }[];
  replySelectedMessage: MessageModel;
  setReplySelectedMessage: (message: MessageModel) => void;
};

// eslint-disable-next-line @typescript-eslint/no-redeclare
export const ChatContext = createContext<ChatContext>({} as ChatContext);

export const ChatProvider: FC = ({ children }) => {
  const [selectedMyChat, setSelectedMyChat] = useState<ChatModel>();
  const [selectedChat, setSelectedChat] = useState<OthersChatModel>();
  const [selectedGroup, setSelectedGroup] = useState<GroupModel>();

  const [isGroupsArchived, setIsGroupsArchived] = useState(false);
  const [isChatsArchived, setIsChatsArchived] = useState(false);
  const [isMyChatsArchived, setIsMyChatsArchived] = useState(false);

  const [activeTab, setActiveTab] = useState<'chats' | 'groups' | 'MyChat'>(
    'MyChat'
  );

  //unread count
  const [gropsUnreadCount, setGroupsUnreadCount] = useState(0);
  const [myChatsUnreadCount, setMyChatsUnreadCount] = useState(0);
  const [chatsUnreadCount, setChatsUnreadCount] = useState(0);

  //reply
  const [replySelectedMessage, setReplySelectedMessage] =
    useState<MessageModel>(null);

  //files
  const [sendingFileChats, setSendingFileChats] = useState([]);

  //add file to in progress files array
  const sendingFileChatAddItem = (
    chatId: number,
    file: File,
    messageTimeStamp,
    axiosSource: CancelTokenSource
  ) => {
    setSendingFileChats((prevState) => [
      ...prevState,
      { id: chatId, progress: 0, file, messageTimeStamp, axiosSource }
    ]);
  };

  //remove item from the array
  const sendingFileChatRemoveItem = (messageTimeStamp) => {
    setSendingFileChats((prev) =>
      prev.filter((chat) => chat.messageTimeStamp != messageTimeStamp)
    );
  };

  //update progress of one file depending on the progress
  const sendingFileChatUpdateProgress = (
    messageTimeStamp: any,
    progress: number
  ) => {
    setSendingFileChats((prevChats) =>
      prevChats.map((chat) => {
        return chat.messageTimeStamp === messageTimeStamp
          ? { ...chat, progress }
          : chat;
      })
    );
  };

  //check if chat has a file in progress
  const isItemSendingFile = (chatId: number) => {
    return sendingFileChats.some((chat) => chat.id == chatId);
  };

  //get all files in progress for a certain chat
  const itemsSendingFile = (chatId: number) => {
    return sendingFileChats.filter((chat) => chat.id == chatId);
  };

  //end files

  const selectMyChat = (chat: ChatModel) => {
    setSelectedMyChat(chat);
    setSelectedGroup(null);
    setSelectedChat(null);
    setReplySelectedMessage(null);
  };
  const selectChat = (chat: OthersChatModel) => {
    setSelectedChat(chat);
    setSelectedGroup(null);
    setSelectedMyChat(null);
    setReplySelectedMessage(null);
  };

  const selectGroup = (group: GroupModel) => {
    setSelectedGroup(group);
    setSelectedMyChat(null);
    setSelectedChat(null);
    setReplySelectedMessage(null);
  };

  return (
    <ChatContext.Provider
      value={{
        selectedMyChat,
        selectedGroup,
        selectedChat,
        activeTab,
        selectMyChat,
        selectGroup,
        selectChat,
        setActiveTab,
        setIsGroupsArchived,
        setIsChatsArchived,
        setIsMyChatsArchived,
        isGroupsArchived,
        isChatsArchived,
        isMyChatsArchived,
        gropsUnreadCount,
        chatsUnreadCount,
        myChatsUnreadCount,
        setGroupsUnreadCount,
        setChatsUnreadCount,
        setMyChatsUnreadCount,
        sendingFileChats,
        setSendingFileChats,
        sendingFileChatAddItem,
        sendingFileChatRemoveItem,
        sendingFileChatUpdateProgress,
        isItemSendingFile,
        itemsSendingFile,
        replySelectedMessage,
        setReplySelectedMessage
      }}
    >
      {children}
    </ChatContext.Provider>
  );
};

export const useChatContext = () => {
  return useContext(ChatContext);
};
